import React, { FC, useMemo } from "react";
import { useTranslation } from "Shared/util/custom-hooks";
import { formatDate } from "Shared/util/formatters";
import { joinClassNames as c } from "Shared/util/styles";
import {
    FinaComputedConsentStatus,
    PayoutInformationAccount,
} from "Shared/util/types/api";
import { isCriticalConsentStatus } from "../utils";
import styles from "./payout-information-settings-card-item.css";

type PayoutInformationSettingsCardItemProps = {
    payoutAccount: PayoutInformationAccount;
};

type ConsentStatusText = {
    redText: boolean;
    text: string;
};

export const useConsentStatusText = (
    payoutAccount: PayoutInformationAccount,
): ConsentStatusText => {
    const { consentStatus, consentExpiresAt } = payoutAccount || {};

    const t = useTranslation(
        (x) => x.company_settings.payoutInformationSettingsCard,
    );

    return useMemo(() => {
        const shouldUseRedText = isCriticalConsentStatus(consentStatus);

        switch (consentStatus) {
            case FinaComputedConsentStatus.NotProvided:
                return {
                    redText: shouldUseRedText,
                    text: t.consentNeedsCreated,
                };

            case FinaComputedConsentStatus.Expired:
                return {
                    redText: shouldUseRedText,
                    text: t.consentNeedsRenewed,
                };

            case FinaComputedConsentStatus.ExpiresSoon:
                return {
                    redText: shouldUseRedText,
                    text: t.format(t.consentValidUntil, {
                        date: formatDate(consentExpiresAt),
                    }),
                };

            case FinaComputedConsentStatus.Provided:
                return {
                    redText: shouldUseRedText,
                    text: t.format(t.consentValidUntil, {
                        date: formatDate(consentExpiresAt),
                    }),
                };

            case FinaComputedConsentStatus.NotRequired:
            default:
                return { redText: shouldUseRedText, text: "" };
        }
    }, [consentExpiresAt, consentStatus, t]);
};

export const PayoutInformationSettingsCardItem: FC<
    PayoutInformationSettingsCardItemProps
> = ({ payoutAccount }) => {
    const { accountNumber, bank, isPrimary } = payoutAccount || {};
    const { name: bankName } = bank || {};

    const t = useTranslation(
        (x) => x.company_settings.payoutInformationSettingsCard,
    );
    const { redText, text } = useConsentStatusText(payoutAccount);

    return (
        <div className={styles.grid}>
            <div className={styles.gridItem}>{bankName}</div>
            <div className={styles.gridItem}>{accountNumber}</div>
            <div className={c(styles.gridItem, redText && styles.redText)}>
                {text}
            </div>
            <div className={styles.gridItem}>
                {isPrimary ? t.defaultAccount : ""}
            </div>
        </div>
    );
};
